import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileText,
  History,
  MessageSquareText,
  RefreshCw,
  RotateCcw,
  Save,
  UserCheck,
  UserCog,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, dateTime } from '../lib/api';
import ProfileVerificationAssessment from '../components/ProfileVerificationAssessment';

const request=(method,body)=>({method,headers:{'content-type':'application/json'},body:JSON.stringify(body)});
const label=value=>String(value||'').replaceAll('_',' ').replace(/\b\w/g,m=>m.toUpperCase());
const localDateTime=value=>{
  if(!value)return '';
  const date=new Date(value);
  if(Number.isNaN(date.getTime()))return '';
  const local=new Date(date.getTime()-date.getTimezoneOffset()*60000);
  return local.toISOString().slice(0,16);
};
const iso=value=>value?new Date(value).toISOString():null;
const safeJson=value=>{try{return value?JSON.parse(value):{};}catch{return{};}};

function dueState(row){
  if(!row?.due_at)return {text:'No internal target',tone:'neutral'};
  if(['resolved','closed'].includes(String(row.status||'').toLowerCase()))return {text:'Completed',tone:'good'};
  const ms=new Date(row.due_at).getTime()-Date.now();
  if(Number.isNaN(ms))return {text:'No internal target',tone:'neutral'};
  const abs=Math.abs(ms),hours=Math.floor(abs/3600000),mins=Math.floor((abs%3600000)/60000),days=Math.floor(hours/24);
  const duration=days?days+'d '+(hours%24)+'h':hours?hours+'h '+mins+'m':Math.max(1,mins)+'m';
  return ms<0?{text:'Overdue by '+duration,tone:'danger'}:ms<4*3600000?{text:duration+' remaining',tone:'warning'}:{text:duration+' remaining',tone:'good'};
}

export default function BackOfficeReferralWorkspace(){
  const{id}=useParams(),navigate=useNavigate();
  const[data,setData]=useState(null),[loading,setLoading]=useState(true),[busy,setBusy]=useState(false),[notice,setNotice]=useState(''),[error,setError]=useState('');
  const[note,setNote]=useState(''),[draft,setDraft]=useState(null);
  const[decisionReason,setDecisionReason]=useState('');
  const[assessmentStatus,setAssessmentStatus]=useState({approvalReady:false,reason:'Complete the verification assessment first.'});
  const[shopDecisionReason,setShopDecisionReason]=useState('');

  const load=useCallback(async()=>{
    setLoading(true);setError('');
    try{
      const payload=await api('/api/back-office/referrals/'+encodeURIComponent(id));
      setData(payload);
      const row=payload.referral||{};
      setDraft({
        title:row.title||'',
        category:row.category||'account',
        urgency:row.urgency||'normal',
        status:row.status||'open',
        assigned_to:row.assigned_to||'back_office',
        due_at:localDateTime(row.due_at),
        waiting_reason:row.waiting_reason||'',
        customer_contact_required:Boolean(row.customer_contact_required),
        reason:row.reason||'',
        resolution:row.resolution||'',
      });
    }catch(e){setError(e.message||String(e));}
    finally{setLoading(false);}
  },[id]);

  useEffect(()=>{void load();},[load]);

  // A Profiles decision is acknowledged asynchronously by its owning service.
  // Refresh the case without unmounting the editor or discarding staff notes.
  useEffect(()=>{
    if(data?.referral?.action_type!=='profiles_verification_review'||
       !(data?.profiles_verification_decision?.status==='queued'||
         (data?.profiles_verification_decision?.status==='failed'&&
          data?.profiles_verification_decision?.transport_code==='verification_request_stale')||
         (['applied','already_verified'].includes(data?.profiles_verification_decision?.status)&&
          data?.profiles_verification_notification?.status!=='sent')))return;
    let stopped=false;
    const refreshDecision=async()=>{
      if(document.visibilityState==='hidden'||busy)return;
      try{
        const updated=await api('/api/back-office/referrals/'+encodeURIComponent(id));
        if(stopped)return;
        setData(updated);
        if(['applied','already_verified'].includes(updated.profiles_verification_decision?.status)){
          setDraft(current=>current?{...current,status:updated.referral?.status||current.status,
            resolution:updated.referral?.resolution||current.resolution}:current);
          setNotice('Sousa Murray Profiles confirmed the verification status. The Back Office record has been synchronised.');
        }
      }catch(error){console.warn('Back Office verification status refresh unavailable',error);}
    };
    const interval=window.setInterval(()=>{void refreshDecision();},3500);
    const onFocus=()=>{void refreshDecision();};
    window.addEventListener('focus',onFocus);
    document.addEventListener('visibilitychange',onFocus);
    return()=>{stopped=true;window.clearInterval(interval);
      window.removeEventListener('focus',onFocus);
      document.removeEventListener('visibilitychange',onFocus);};
  },[id,data?.referral?.action_type,data?.profiles_verification_decision?.status,
    data?.profiles_verification_decision?.transport_code,
    data?.profiles_verification_notification?.status,busy]);

  const referral=data?.referral;
  const due=useMemo(()=>dueState(referral),[referral]);
  const set=(key,value)=>setDraft(current=>({...current,[key]:value}));

  const mutate=async(fn,success)=>{
    setBusy(true);setNotice('');setError('');
    try{
      const result=await fn();
      setNotice(typeof success==='function'?success(result):success);
      await load();
      return result;
    }
    catch(e){setError(e.message||String(e));return null;}
    finally{setBusy(false);}
  };
  const assignmentNotice=(base,result)=>{
    const notification=result?.assignment_notification;
    if(notification?.status==='sent')return base+' Assignment email sent to '+(notification.staff_name||result?.referral?.assigned_to_name||'the assignee')+'.';
    if(notification?.status==='queued_for_retry')return base+' Assignment email queued for automatic retry.';
    return base;
  };

  const save=()=>{
    if(data?.referral?.action_type==='profiles_verification_review'&&
       ['resolved','closed'].includes(draft?.status)&&
       data?.profiles_verification_decision?.status!=='applied'){
      setError('Changing the referral status does not verify the customer. Use the Approve verification, Reject, or Request more information controls in the Verification review workspace first.');
      document.getElementById('profile-verification-review')?.scrollIntoView({behavior:'smooth',block:'start'});
      return;
    }
    return mutate(
    ()=>api('/api/back-office/referrals/'+encodeURIComponent(id),request('PATCH',{
      title:draft.title,
      category:draft.category,
      urgency:draft.urgency,
      status:draft.status,
      assigned_to:draft.assigned_to,
      due_at:iso(draft.due_at),
      waiting_reason:draft.waiting_reason,
      customer_contact_required:draft.customer_contact_required,
      reason:draft.reason,
      resolution:draft.resolution,
    })),
    result=>assignmentNotice('Back Office referral updated.',result)
  );
  };

  const claim=()=>mutate(
    ()=>api('/api/back-office/referrals/'+encodeURIComponent(id)+'/claim',request('POST',{})),
    result=>assignmentNotice('Referral assigned to you.',result)
  );

  const quickStatus=status=>{
    const payload={status};
    if(['resolved','closed'].includes(status)){
      if(!draft.resolution.trim()){setError('Record the resolution before resolving or closing this referral.');return;}
      payload.resolution=draft.resolution;
    }
    if(status==='waiting_customer')payload.waiting_reason=draft.waiting_reason||'Waiting for customer response.';
    return mutate(()=>api('/api/back-office/referrals/'+encodeURIComponent(id),request('PATCH',payload)),status==='resolved'?'Referral resolved.':status==='closed'?'Referral closed.':status==='open'?'Referral reopened.':status==='waiting_customer'?'Referral set to waiting for customer.':'Referral moved to in progress.');
  };

  const addNote=()=>{
    if(note.trim().length<2){setError('Enter an internal note first.');return;}
    return mutate(async()=>{
      await api('/api/back-office/referrals/'+encodeURIComponent(id)+'/notes',request('POST',{note:note.trim()}));
      setNote('');
    },'Internal note added.');
  };

  const decideVerification=decision=>{
    const names={approve:'approve',reject:'reject',more_info:'request further information'};
    if(decision==='approve'&&!assessmentStatus.approvalReady){setError(assessmentStatus.reason||'Complete the verification assessment first.');return;}
    if(assessmentStatus.evidence?.conflictOfInterest){setError('Assign the case to an independent reviewer before making a decision.');return;}
    if(assessmentStatus.unsaved){setError('Save your assessment changes before recording the decision.');return;}
    if(!window.confirm('Confirm you want to '+names[decision]+' verification for this Profile?'))return;
    return (async()=>{
      setBusy(true);setNotice('');setError('');
      try{
        const result=await api('/api/back-office/referrals/'+encodeURIComponent(id)+'/profiles-verification',
          request('POST',{decision,reason:decisionReason.trim()}));
        setDecisionReason('');
        await load();
        if(result?.decisionStatus==='applied')setNotice('Verification decision confirmed by Sousa Murray Profiles.');
        else if(result?.sync&&!result.sync.ok)setError(result.message||
          'Decision queued, but Sousa Murray Profiles did not respond. The customer has NOT been verified.');
        else setNotice('Verification decision securely queued. Back Office is waiting for Profiles confirmation.');
      }catch(e){setError(e?.message||'Could not submit the verification decision.');}
      finally{setBusy(false);}
    })();
  };

  const retryProfilesSync=async()=>{
    setBusy(true);setNotice('');setError('');
    try{
      const result=await api('/api/back-office/referrals/'+encodeURIComponent(id)+'/profiles-verification/sync',request('POST',{}));
      await load();
      const text=result?.message||'Verification synchronisation checked.';
      if(['applied','already_verified'].includes(result?.decision?.status))setNotice(text);
      else if(result?.decision?.status==='failed'||(result?.wake&&!result.wake.ok))
        setError(text); // Never display a failed Profiles connection in a green success banner.
      else setNotice(text);
    }catch(e){setError(e?.message||'Could not contact Sousa Murray Profiles.');}
    finally{setBusy(false);}
  };

  const retryDecisionEmail=async()=>{
    setBusy(true);setNotice('');setError('');
    try{
      const result=await api('/api/back-office/referrals/'+encodeURIComponent(id)+
        '/profiles-verification/notification/retry',request('POST',{}));
      await load();
      if(result?.notification?.status==='sent')setNotice(result.message);
      else setError(result?.message||'Verification decision email is awaiting delivery.');
    }catch(e){setError(e?.message||'Could not retry the verification decision email.');}
    finally{setBusy(false);}
  };

  const decideShopApproval=decision=>{
    if(shopDecisionReason.trim().length<10){setError('Record at least 10 characters explaining your decision.');return;}
    const verb=decision==='approve'?'AUTHORISE':'DECLINE';
    if(!window.confirm(verb+' this high-value Shop checkout? This changes the actual payment hold.'))return;
    return mutate(
      ()=>api('/api/back-office/referrals/'+encodeURIComponent(id)+'/shop-approval',
        request('POST',{decision,reason:shopDecisionReason.trim()})),
      result=>{setShopDecisionReason('');return result?.decision==='approved'?
        'Shop order approved. The customer may resume the existing checkout before it expires; no payment was collected by this action.':
        'Shop order declined and payment remains blocked.';}
    );
  };

  if(loading)return <div className="back-office-workspace-state">Loading Back Office referral…</div>;
  if(!referral||!draft)return <div className="back-office-workspace-state error">Error: {error||'Back Office referral not found.'}</div>;

  const completed=['resolved','closed'].includes(referral.status);
  const assignees=data?.assignees||[];
  const myAssignmentId=data.current_staff?.back_office_assignment_id||data.current_staff?.id;
  const mine=myAssignmentId&&String(referral.assigned_to)===String(myAssignmentId);
  const isProfilesVerification=referral.action_type==='profiles_verification_review';
  const profilesDecision=data?.profiles_verification_decision||null;
  const profilesNotification=data?.profiles_verification_notification||null;
  const decisionPending=profilesDecision?.status==='queued';
  const verificationDecisionConfirmed=['applied','already_verified'].includes(profilesDecision?.status);
  const staleVerificationApplication=profilesDecision?.status==='failed'&&
    profilesDecision?.transport_code==='verification_request_stale';
  const isShopApproval=referral.action_type==='shop_order_approval';
  const shopApproval=data?.shop_approval||null;
  const pounds=value=>'£'+(Number(value||0)/100).toFixed(2);
  const profileMatch=String(referral.title||'').match(/SMP-\d{8}/);
  const profileReference=profileMatch?.[0]||'See referral details';


  return <div className="back-office-workspace">
    <header className="back-office-workspace-header">
      <div>
        <button className="back-office-back" type="button" onClick={()=>navigate('/back-office')}><ArrowLeft size={14}/>Back Office</button>
        <div className="back-office-workspace-eyebrow"><span>{referral.reference}</span><span className={'back-office-status status-'+referral.status}>{label(referral.status)}</span></div>
        <h1 className="govuk-heading-xl">{referral.title}</h1>
        <p className="govuk-body">{label(referral.category)} · {label(referral.action_type)} · Raised {dateTime(referral.created_at)}</p>
      </div>
      <div className="back-office-workspace-actions">
        <button className="button secondary govuk-button govuk-button--secondary" onClick={load} disabled={busy}><RefreshCw size={14}/>Refresh</button>
        {!mine&&!completed&&<button className="button primary govuk-button" onClick={claim} disabled={busy}><UserCheck size={14}/>Assign to me</button>}
      </div>
    </header>

    {(notice||error)&&<div className={'operation-notice '+(error?'error':'')}>{error?('Error: '+error):notice}</div>}

    {isProfilesVerification&&!completed&&<div className="back-office-decision-callout" role="status">
      <div>
        <strong>{decisionPending?'Awaiting confirmation from Sousa Murray Profiles':profilesDecision?.status==='failed'?'Verification decision was not applied':'Verification needs a Back Office decision'}</strong>
        <p className="govuk-body">{decisionPending
          ?'A decision has been sent to Profiles. Its result updates automatically here. Do not close the referral while confirmation is pending.'
          :staleVerificationApplication
            ?'Profiles says the original application is no longer pending. Another action may already have verified this Profile. Recheck its current state; do not approve it again.'
          :profilesDecision?.status==='failed'
            ?'Profiles rejected the last decision. Review the failure in the verification workspace before attempting a new action.'
            :'Selecting Resolved in Referral details does NOT approve verification. Complete the evidence review and use the dedicated decision buttons instead.'}</p>
      </div>
      <button type="button" className="button primary govuk-button" onClick={()=>
        document.getElementById('profile-verification-review')?.scrollIntoView({behavior:'smooth',block:'start'})
      }>{decisionPending?'View verification progress':'Review and decide verification'}</button>
    </div>}
    <section className="back-office-commandbar">
      {!completed&&<>
        <button type="button" onClick={()=>quickStatus('in_progress')} disabled={busy||referral.status==='in_progress'}><UserCog size={13}/>In progress</button>
        <button type="button" onClick={()=>quickStatus('waiting_customer')} disabled={busy||referral.status==='waiting_customer'}><Clock3 size={13}/>Waiting customer</button>
        <button type="button" onClick={()=>quickStatus('resolved')} disabled={busy||(isProfilesVerification&&!verificationDecisionConfirmed)||(isShopApproval&&shopApproval?.status==='pending')} title={isProfilesVerification&&profilesDecision?.status!=='applied'?'Confirm the Profiles decision first.':undefined}><CheckCircle2 size={13}/>Resolve</button>
        <button type="button" onClick={()=>quickStatus('closed')} disabled={busy||(isProfilesVerification&&!verificationDecisionConfirmed)||(isShopApproval&&shopApproval?.status==='pending')}>Close</button>
      </>}
      {completed&&<button type="button" onClick={()=>quickStatus('open')} disabled={busy}><RotateCcw size={13}/>Reopen</button>}
      <button type="button" onClick={()=>navigate('/customers/'+encodeURIComponent(referral.customer_id))}><ExternalLink size={13}/>Customer360</button>
      {referral.source_case_id&&!String(referral.source_case_id).startsWith('profiles:')&&!String(referral.source_case_id).startsWith('shop:approval:')&&!String(referral.source_case_id).startsWith('service-ops:')&&<button type="button" onClick={()=>navigate('/cases/'+encodeURIComponent(referral.source_case_id))}><FileText size={13}/>Source case</button>}
    </section>

    <div className="back-office-workspace-grid">
      <main className="back-office-workspace-main">
        {isProfilesVerification&&<section id="profile-verification-review" className="back-office-card back-office-verification-panel" aria-label="Profile verification investigation">
          <header><div><span>PROFILE VERIFICATION</span><h2 className="govuk-heading-m">Verification review workspace</h2></div></header>
          <div className="verification-reference">
            <div><span>Permanent Profile ID</span><strong>{profileReference}</strong></div>
            <div><span>Application</span><strong>{label(referral.action_type)}</strong></div>
            <div><span>Customer</span><strong>{referral.customer_name||referral.customer_number||'Customer'}</strong></div>
            <div><span>Current case owner</span><strong>{referral.assigned_to_name||'Unassigned'}</strong></div>
          </div>
          <ProfileVerificationAssessment referralId={id} onStatusChange={setAssessmentStatus}>
          <div className="back-office-verification-controls">
            <label className="govuk-label" htmlFor="back-office-verification-reason">Optional decision note</label>
            <textarea className="govuk-textarea" id="back-office-verification-reason" rows={4} value={decisionReason}
              onChange={e=>setDecisionReason(e.target.value)}
              placeholder="Optional: explain your decision or describe what information is required. No minimum length."/>
            <div className="profile-verification-outcome-grid">
              <button type="button" className="profile-verification-outcome approve" disabled={busy||completed||decisionPending||verificationDecisionConfirmed||staleVerificationApplication||assessmentStatus.unsaved||!assessmentStatus.approvalReady} title={!assessmentStatus.approvalReady?assessmentStatus.reason:undefined} onClick={()=>decideVerification('approve')}>
                <strong>Approve verification</strong><span>Evidence and ownership checks passed; submit for service confirmation.</span>
              </button>
              <button type="button" className="profile-verification-outcome more-info" disabled={busy||completed||decisionPending||verificationDecisionConfirmed||staleVerificationApplication||assessmentStatus.unsaved||assessmentStatus.evidence?.conflictOfInterest} onClick={()=>decideVerification('more_info')}>
                <strong>Request more information</strong><span>Keep the application on hold while awaiting evidence.</span>
              </button>
              <button type="button" className="profile-verification-outcome reject" disabled={busy||completed||decisionPending||verificationDecisionConfirmed||staleVerificationApplication||assessmentStatus.unsaved||assessmentStatus.evidence?.conflictOfInterest} onClick={()=>decideVerification('reject')}>
                <strong>Reject verification</strong><span>Decline the application following staff review.</span>
              </button>
            </div>
            <small>The owning Profiles service applies the decision after confirmation. All outcomes are audited. The optional note can be left blank; an assessment-based reason is recorded automatically.</small>
          </div>
          </ProfileVerificationAssessment>
          {profilesDecision&&<div className={'operation-notice'+(profilesDecision.status==='failed'?' error':'')} role="status">
            <strong>Service decision: {label(profilesDecision.status)}</strong>
            {profilesDecision.status==='queued'&&<p className="govuk-body">The request is awaiting confirmation from Sousa Murray Profiles. Do not submit another decision while it is processing. Its delivery and confirmation state are shown below.</p>}
            {profilesDecision.status!=='already_verified'&&profilesDecision.transport_status&&<p className="govuk-body"><strong>Connector transport:</strong> {label(profilesDecision.transport_status)}
              {profilesDecision.transport_delivered_at?' · Delivered '+dateTime(profilesDecision.transport_delivered_at):''}
              {profilesDecision.transport_acknowledged_at?' · Acknowledged '+dateTime(profilesDecision.transport_acknowledged_at):''}</p>}
            {profilesDecision.status!=='already_verified'&&profilesDecision.transport_message&&<p className="govuk-body"><strong>Profiles response:</strong> {profilesDecision.transport_message}</p>}
            {profilesDecision.status!=='already_verified'&&profilesDecision.transport_code&&<p className="govuk-body"><strong>Service code:</strong> {profilesDecision.transport_code}</p>}
            {profilesDecision.status==='queued'&&<button type="button" className="button secondary govuk-button govuk-button--secondary"
              onClick={retryProfilesSync} disabled={busy}><RefreshCw size={14}/>Retry verification sync now</button>}
            {staleVerificationApplication&&<div className="verification-guidance">
              <strong>This verification application is no longer pending in Profiles.</strong>
              {' '}The customer may already have been verified or the application may have changed.
              Do not submit another approval. Check the owning Profile's current status and reconcile the case with its authenticated Customer360 snapshot.
              <button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={retryProfilesSync} disabled={busy}>
                <RefreshCw size={14}/>Recheck verified Profile status
              </button>
            </div>}
            {profilesDecision.status==='failed'&&!staleVerificationApplication&&<p className="govuk-body">Profiles did not apply the decision: {profilesDecision.error_message||'Review the service error before resubmitting.'}</p>}
            {profilesDecision.status==='applied'&&<p className="govuk-body">This Back Office decision was confirmed and applied by Sousa Murray Profiles.</p>}
            {profilesDecision.status==='already_verified'&&<p className="govuk-body"><strong>Already verified in Profiles.</strong> The owning service's current Customer360 snapshot confirms this customer Profile is verified. The earlier Back Office command is now obsolete. The case has been reconciled without repeating verification or incorrectly attributing the existing approval to the reviewer. The unsuccessful historical command remains available in the case audit history.</p>}
          </div>}
          {(verificationDecisionConfirmed||profilesNotification)&&<div className="back-office-verification-email-status" role="status">
            <strong>Customer verification decision email</strong>
            {profilesNotification?.status==='sent'
              ?<p className="govuk-body">✅ Accepted for sending by Microsoft 365{profilesNotification.sentAt?' · '+dateTime(profilesNotification.sentAt):''}. A record is retained in Customer360 communications. Delivery to the recipient's inbox cannot be guaranteed by the sending API.</p>
              :<p className="govuk-body">{verificationDecisionConfirmed
                ?'The customer verification decision is confirmed, but there is no completed email send recorded yet.'
                :'The verification decision has not been confirmed by Profiles. No final decision email can be issued yet.'}
                {profilesNotification?.status==='retry'?' Automatic retries are pending.':''}</p>}
            {profilesNotification?.status!=='sent'&&profilesNotification?.lastError&&
              <p className="govuk-body"><strong>Mail transport error:</strong> {profilesNotification.lastError}</p>}
            {verificationDecisionConfirmed&&profilesNotification?.status!=='sent'&&
              <button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={retryDecisionEmail} disabled={busy}>
                <RefreshCw size={14}/>Send or retry decision email
              </button>}
          </div>}
          <div className="verification-reference">
            <button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={()=>navigate('/customers/'+encodeURIComponent(referral.customer_id))}>Open linked Customer360 record <ExternalLink size={14}/></button>
            <button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={()=>
              document.getElementById('back-office-verification-reason')?.focus()
            } disabled={busy||completed||decisionPending}>Document evidence request <Clock3 size={14}/></button>
          </div>
        </section>}
        <section className="back-office-card">
          <header><div><span>REFERRAL</span><h2 className="govuk-heading-m">Requested Back Office work</h2></div></header>
          <div className="back-office-reason"><p className="govuk-body">{referral.reason}</p></div>
          <div className="back-office-context-grid">
            <div><span>Customer</span><strong>{referral.customer_name||'Customer'}</strong><small>{referral.customer_number||referral.customer_id}</small></div>
            <div><span>Raised by</span><strong>{referral.raised_by_name||'Staff'}</strong><small>{dateTime(referral.created_at)}</small></div>
            <div><span>Assigned to</span><strong>{referral.assigned_to_name||'Unassigned'}</strong><small>{referral.assigned_to||'unassigned'}</small></div>
            <div><span>Internal target</span><strong className={'back-office-due '+due.tone}>{due.text}</strong><small>{referral.due_at?dateTime(referral.due_at):'Not set'}</small></div>
          </div>
        </section>

        {isShopApproval&&<section className="back-office-card back-office-verification-panel" aria-label="Large Shop purchase approval">
          <header><div><span>SHOP AUTHORISATION</span><h2 className="govuk-heading-m">High-value purchase decision</h2></div></header>
          <p className="verification-guidance govuk-body"><strong>Payment is held.</strong> The checkout value meets or exceeds £500. Review the order, customer, payment risk and any unusual purchasing pattern before authorising. Authorisation allows the customer to resume payment; it does not charge their card or fulfil the order.</p>
          <div className="verification-reference">
            <div><span>Customer account</span><strong>{referral.customer_name||referral.customer_number||'Customer'}</strong></div>
            <div><span>Held checkout</span><strong>{String(referral.source_case_id||'').replace('shop:approval:','')}</strong></div>
            <div><span>Requested work</span><strong>Review and approve or decline</strong></div>
            <div><span>Assigned reviewer</span><strong>{referral.assigned_to_name||'Back Office'}</strong></div>
          </div>
          {shopApproval&&<div className="back-office-shop-order-review">
            <div className="back-office-shop-review-summary">
              <div><span>Total under review</span><strong>{pounds(shopApproval.amountMinor)}</strong></div>
              <div><span>Authorisation status</span><strong>{label(shopApproval.status)}</strong></div>
              <div><span>Checkout expires</span><strong>{dateTime(shopApproval.expiresAt)}</strong></div>
              <div><span>Delivery included</span><strong>{pounds(shopApproval.shippingMinor)}</strong></div>
            </div>
            <h3 className="govuk-heading-s">Items in the held purchase</h3>
            <div className="back-office-shop-order-lines">
              {(shopApproval.items||[]).map((line,index)=><div key={index}>
                <div><strong>{line.title||'Shop product'}</strong><small>Quantity {line.quantity} · {pounds(line.unitMinor)} each</small></div>
                <strong>{pounds(line.lineTotalMinor)}</strong>
              </div>)}
            </div>
            <p className="govuk-body">Only the saved product summary is shown. Checkout credentials and payment tokens are not disclosed to staff.</p>
            {shopApproval.decisionReason&&<p className="govuk-body"><strong>Recorded decision:</strong> {shopApproval.decisionReason}</p>}
          </div>}
          <div className="back-office-verification-controls">
            <label className="govuk-label" htmlFor="shop-approval-decision-reason">Assessment and decision reason</label>
            <textarea className="govuk-textarea" id="shop-approval-decision-reason" rows={4} value={shopDecisionReason}
              onChange={e=>setShopDecisionReason(e.target.value)}
              placeholder="Document what was checked, relevant fraud indicators, customer contact and your decision"/>
            <div className="back-office-verification-buttons">
              <button className="button primary govuk-button" type="button" disabled={busy||completed||shopApproval?.status!=='pending'} onClick={()=>decideShopApproval('approve')}>Authorise payment</button>
              <button className="button secondary govuk-button govuk-button--secondary" type="button" disabled={busy||completed||shopApproval?.status!=='pending'} onClick={()=>decideShopApproval('reject')}>Decline checkout</button>
              <button className="button secondary govuk-button govuk-button--secondary" type="button" onClick={()=>navigate('/customers/'+encodeURIComponent(referral.customer_id))}>Customer360 <ExternalLink size={14}/></button>
            </div>
            <small>Only staff with Back Office/payment write authority can decide. A rejected or expired checkout cannot be paid without a new reviewed request.</small>
          </div>
        </section>}
        <section className="back-office-card">
          <header><div><span>NOTES</span><h2 className="govuk-heading-m">Internal Back Office notes</h2></div><MessageSquareText size={17}/></header>
          <div className="back-office-note-composer">
            <textarea className="govuk-textarea" rows="4" value={note} onChange={e=>setNote(e.target.value)} placeholder="Add investigation notes, what has been checked, decisions, or what needs to happen next…"/>
            <div><span>Internal to Customer Operations / Back Office.</span><button className="button primary small govuk-button" onClick={addNote} disabled={busy||note.trim().length<2}>Add note</button></div>
          </div>
          <div className="back-office-note-list">
            {(data.notes||[]).length?(data.notes||[]).map(item=><article key={item.id}><div><strong>{item.created_by_name||'Staff'}</strong><time>{dateTime(item.created_at)}</time></div><p className="govuk-body">{item.note_text}</p></article>):<div className="back-office-inline-empty">No internal notes have been added.</div>}
          </div>
        </section>

        <section className="back-office-card">
          <header><div><span>AUDIT</span><h2 className="govuk-heading-m">Activity history</h2></div><History size={17}/></header>
          <div className="back-office-activity-list">
            {(data.events||[]).length?(data.events||[]).map(item=>{
              const meta=safeJson(item.metadata_json);
              return <article key={item.id}><span className="back-office-activity-dot"/><div><strong>{item.summary}</strong><small>{label(item.event_type)} · {item.actor_name||'System'} · {dateTime(item.created_at)}</small>{meta.changes&&<p className="govuk-body">{Object.keys(meta.changes).map(label).join(', ')} changed</p>}</div></article>;
            }):<div className="back-office-inline-empty">No activity has been recorded yet.</div>}
          </div>
        </section>

        {(data.related_referrals||[]).length>0&&<section className="back-office-card">
          <header><div><span>RELATED</span><h2 className="govuk-heading-m">Other referrals for this customer</h2></div></header>
          <div className="back-office-related-list">{data.related_referrals.map(item=><button type="button" key={item.id} onClick={()=>navigate('/back-office/'+encodeURIComponent(item.id))}><div><strong>{item.reference}</strong><span>{item.title}</span></div><small>{label(item.status)} · {dateTime(item.created_at)}</small></button>)}</div>
        </section>}
      </main>

      <aside className="back-office-workspace-side">
        <section className="back-office-card back-office-edit-card">
          <header><div><span>CONTROL</span><h2 className="govuk-heading-m">Referral details</h2></div></header>
          <label className="govuk-label"><span>Title</span><input className="govuk-input" value={draft.title} onChange={e=>set('title',e.target.value)}/></label>
          <div className="back-office-field-row">
            <label className="govuk-label"><span>Category</span><select className="govuk-select" value={draft.category} onChange={e=>set('category',e.target.value)}><option value="account">Account</option><option value="identity">Identity</option><option value="portal">Portal</option><option value="security">Security</option><option value="billing">Billing</option><option value="collections">Collections</option><option value="data_correction">Data correction</option><option value="technical">Technical</option><option value="other">Other</option></select></label>
            <label className="govuk-label"><span>Urgency</span><select className="govuk-select" value={draft.urgency} onChange={e=>set('urgency',e.target.value)}><option value="normal">Normal</option><option value="urgent">Urgent</option><option value="critical">Critical</option></select></label>
          </div>
          <label className="govuk-label"><span>Assignment</span><select className="govuk-select" value={draft.assigned_to} onChange={e=>set('assigned_to',e.target.value)}><option value="auto">Automatic — senior Back Office</option>{assignees.map(person=><option key={person.id} value={person.id}>{person.name} · {Number(person.open_count||0)} open</option>)}<option value="unassigned">Unassigned</option>{referral.assigned_to&&!['alfie','jack','back_office','unassigned'].includes(String(referral.assigned_to))&&!assignees.some(person=>String(person.id)===String(referral.assigned_to))&&<option value={referral.assigned_to}>{referral.assigned_to_name||referral.assigned_to}</option>}</select><small>Choose Automatic to rebalance the referral to a senior Back Office member.</small></label>
          <label className="govuk-label"><span>Status</span><select className="govuk-select" value={draft.status} onChange={e=>set('status',e.target.value)}><option value="open">Open</option><option value="in_progress">In progress</option><option value="waiting_customer">Waiting customer</option><option value="resolved" disabled={isProfilesVerification&&!verificationDecisionConfirmed}>Resolved</option><option value="closed" disabled={isProfilesVerification&&!verificationDecisionConfirmed}>Closed</option></select>{isProfilesVerification&&!verificationDecisionConfirmed&&<small>The status cannot be resolved or closed until Profiles confirms an actual verification decision. Use the review controls, not this field.</small>}</label>
          <label className="govuk-label"><span>Internal target</span><input className="govuk-input" type="datetime-local" value={draft.due_at} onChange={e=>set('due_at',e.target.value)}/></label>
          <label className="govuk-label"><span>Waiting reason</span><textarea className="govuk-textarea" rows="3" value={draft.waiting_reason} onChange={e=>set('waiting_reason',e.target.value)} placeholder="What are we waiting for?"/></label>
          <label className="back-office-check govuk-label"><input type="checkbox" checked={draft.customer_contact_required} onChange={e=>set('customer_contact_required',e.target.checked)}/><span>Customer contact is required</span></label>
          <label className="govuk-label"><span>Referral reason</span><textarea className="govuk-textarea" rows="5" value={draft.reason} onChange={e=>set('reason',e.target.value)}/></label>
          <label className="govuk-label"><span>Resolution</span><textarea className="govuk-textarea" rows="5" value={draft.resolution} onChange={e=>set('resolution',e.target.value)} placeholder="Required before resolving or closing."/></label>
          <button className="button primary back-office-save govuk-button" onClick={save} disabled={busy||(isProfilesVerification&&!verificationDecisionConfirmed&&['resolved','closed'].includes(draft.status))}><Save size={14}/>Save details</button>
        </section>

        <section className="back-office-card back-office-meta-card">
          <header><div><span>RECORD</span><h2 className="govuk-heading-m">Referral information</h2></div></header>
          <dl>
            <div><dt>Reference</dt><dd>{referral.reference}</dd></div>
            <div><dt>Action</dt><dd>{label(referral.action_type)}</dd></div>
            <div><dt>Source</dt><dd>{label(referral.source_channel||'customer_operations')}</dd></div>
            <div><dt>Created</dt><dd>{dateTime(referral.created_at)}</dd></div>
            <div><dt>Last activity</dt><dd>{dateTime(referral.last_activity_at||referral.updated_at)}</dd></div>
            {referral.resolved_at&&<div><dt>Resolved</dt><dd>{dateTime(referral.resolved_at)} by {referral.resolved_by_name||'Staff'}</dd></div>}
            {referral.reopened_at&&<div><dt>Last reopened</dt><dd>{dateTime(referral.reopened_at)}</dd></div>}
          </dl>
        </section>

        {referral.urgency==='critical'&&<div className="back-office-critical-warning"><AlertTriangle size={16}/><div><strong>Critical referral</strong><span>Keep the investigation record current and escalate immediately if customer security or access is at risk.</span></div></div>}
      </aside>
    </div>
  </div>;
}
